import * as THREE from 'three';
import { lerp } from '../core/math';
import type { WeaponId } from '../weapons/WeaponDefs';

export type ArmGripStyle = 'm9' | 'm870' | 'sawedoff' | 'longGun' | 'fists';
export type HandSide = 'R' | 'L';

/** Local attach on the grip socket. +Y = back of hand, +Z = wrist, −Z = fingers. */
export interface HandAttachPose {
  x: number;
  y: number;
  z: number;
  rx: number;
  ry: number;
  rz: number;
  /** Wrist joint, applied after hand rx/ry/rz. */
  wx: number;
  wy: number;
  wz: number;
  scale: number;
  /** Extra finger wrap (radians, added to every joint). */
  curl: number;
  /** Extra wrap on ring + pinky first joints only (MCP). */
  knuckle: number;
}

export type HandAttachSet = { R: HandAttachPose; L: HandAttachPose };

type Pose3 = [number, number, number];

interface Finger {
  bones: THREE.Group[];
  restYaw: number;
}

interface HandRig {
  hand: THREE.Group;
  wrist: THREE.Group;
  palm: THREE.Group;
  palmCap: THREE.Mesh;
  fingers: Finger[];
  thumb: Finger;
  forearm: THREE.Mesh;
  sleeve: THREE.Mesh;
  shoulder: THREE.Mesh;
  shoulderLocal: THREE.Vector3;
  palmBindZ: number;
}

const HAND_SCALE = 1.12;

export function armStyleForWeapon(id: WeaponId): ArmGripStyle {
  if (id === 'pistol') return 'm9';
  if (id === 'punch') return 'fists';
  return 'longGun';
}

export function defaultHandAttachPose(): HandAttachPose {
  return { x: 0, y: 0.011, z: 0.006, rx: 0, ry: 0, rz: 0, wx: 0, wy: 0, wz: 0, scale: HAND_SCALE, curl: 0, knuckle: 0 };
}

export function defaultHandAttachSet(): HandAttachSet {
  return { R: defaultHandAttachPose(), L: defaultHandAttachPose() };
}

function clonePose(p: HandAttachPose): HandAttachPose {
  return { ...p };
}

function cloneAttachSet(s: HandAttachSet): HandAttachSet {
  return { R: clonePose(s.R), L: clonePose(s.L) };
}

/** Right +X is pinky / world-down, so negative x lifts the fist onto the handle. */
const GRIP_R: HandAttachPose = {
  x: -0.028,
  y: -0.008,
  z: 0.024,
  rx: -0.28,
  ry: 0.1,
  rz: -0.16,
  wx: 0.05,
  wy: 0,
  wz: 0.05,
  scale: 1.06,
  curl: -0.36,
  knuckle: -0.12,
};

const PISTOL_L: HandAttachPose = {
  x: 0.008,
  y: 0.014,
  z: 0.022,
  rx: 0.22,
  ry: -0.18,
  rz: 0.42,
  wx: 0.1,
  wy: -0.08,
  wz: -0.16,
  scale: 1.08,
  curl: -0.52,
  knuckle: -0.18,
};

const FOREND_L: HandAttachPose = {
  x: 0.004,
  y: -0.012,
  z: 0.022,
  rx: 0.12,
  ry: 0.04,
  rz: 0.1,
  wx: 0.16,
  wy: 0,
  wz: 0.08,
  scale: 1.1,
  curl: -0.46,
  knuckle: -0.14,
};

const PISTOL_HIP_ATTACH: HandAttachSet = { R: clonePose(GRIP_R), L: clonePose(PISTOL_L) };
const PISTOL_ADS_ATTACH: HandAttachSet = {
  R: { ...GRIP_R, curl: -0.48, knuckle: -0.12 },
  L: { ...PISTOL_L, curl: -0.5, knuckle: -0.14 },
};

const MP5_HIP_ATTACH: HandAttachSet = { R: clonePose(GRIP_R), L: clonePose(FOREND_L) };
const MP5_ADS_ATTACH: HandAttachSet = {
  R: { ...GRIP_R, curl: -0.48, knuckle: -0.12 },
  L: { ...FOREND_L, curl: -0.56, knuckle: -0.18 },
};

const SAWEDOFF_HIP_ATTACH: HandAttachSet = { R: clonePose(GRIP_R), L: clonePose(FOREND_L) };
const SAWEDOFF_ADS_ATTACH: HandAttachSet = {
  R: { ...GRIP_R, curl: -0.48, knuckle: -0.12 },
  L: { ...FOREND_L, y: -0.012, curl: -0.54, knuckle: -0.16 },
};

/** Palm-down hold for the break-reload dummy (not the forend attach). */
export const SAWEDOFF_RELOAD_PALM: HandAttachPose = {
  x: 0,
  y: 0.005,
  z: 0.01,
  rx: 0.18,
  ry: 0.04,
  rz: 0.1,
  wx: 0.22,
  wy: 0,
  wz: 0,
  scale: 1.28,
  curl: 0.48,
  knuckle: 0.22,
};

export function bakedReloadPalm(): HandAttachPose {
  return clonePose(SAWEDOFF_RELOAD_PALM);
}

const AK47_HIP_ATTACH: HandAttachSet = { R: clonePose(GRIP_R), L: clonePose(FOREND_L) };
const AK47_ADS_ATTACH: HandAttachSet = {
  R: { ...GRIP_R, curl: -0.5, knuckle: -0.14 },
  L: { ...FOREND_L, y: -0.012, curl: -0.56, knuckle: -0.18 },
};

const PUNCH_HIP_ATTACH: HandAttachSet = {
  R: { x: -0.01, y: 0.075, z: -0.22, rx: 0.57, ry: 0.29, rz: -0.97, wx: 0.1, wy: 0, wz: 0.05, scale: 1.22, curl: -0.8, knuckle: 0 },
  L: { x: 0, y: 0.095, z: -0.22, rx: 0.86, ry: -0.36, rz: 0.86, wx: 0.07, wy: 0, wz: -0.05, scale: 1.22, curl: -0.55, knuckle: -0.12 },
};

const GL_HIP_ATTACH: HandAttachSet = { R: clonePose(GRIP_R), L: clonePose(FOREND_L) };
const GL_ADS_ATTACH: HandAttachSet = {
  R: { ...GRIP_R, curl: -0.48, knuckle: -0.12 },
  L: { ...FOREND_L, curl: -0.54, knuckle: -0.16 },
};

const RPG_HIP_ATTACH: HandAttachSet = { R: clonePose(GRIP_R), L: clonePose(FOREND_L) };
const RPG_ADS_ATTACH: HandAttachSet = {
  R: { ...GRIP_R, curl: -0.48, knuckle: -0.12 },
  L: { ...FOREND_L, curl: -0.54, knuckle: -0.16 },
};

const PUNCH_ADS_ATTACH: HandAttachSet = {
  R: { x: -0.07, y: -0.058, z: 0.103, rx: 1.11, ry: 1.33, rz: 0.08, wx: 0.1, wy: 0, wz: 0.05, scale: 1.22, curl: -0.55, knuckle: -0.12 },
  L: { x: 0.068, y: -0.015, z: 0.103, rx: 1.08, ry: -1.04, rz: -0.08, wx: 0.1, wy: 0, wz: -0.05, scale: 1.22, curl: -0.55, knuckle: -0.12 },
};

export function bakedAttachSet(id: WeaponId): HandAttachSet {
  if (id === 'punch') return cloneAttachSet(PUNCH_HIP_ATTACH);
  if (id === 'rpg') return cloneAttachSet(RPG_HIP_ATTACH);
  if (id === 'ak47' || id === 'shotgun') return cloneAttachSet(AK47_HIP_ATTACH);
  if (id === 'gl') return cloneAttachSet(GL_HIP_ATTACH);
  if (id === 'sawedoff') return cloneAttachSet(SAWEDOFF_HIP_ATTACH);
  if (id === 'mp5') return cloneAttachSet(MP5_HIP_ATTACH);
  return cloneAttachSet(PISTOL_HIP_ATTACH);
}

export function bakedAttachPose(id: WeaponId, side: HandSide): HandAttachPose {
  return clonePose(bakedAttachSet(id)[side]);
}

export function bakedAdsAttachSet(id: WeaponId): HandAttachSet {
  if (id === 'punch') return cloneAttachSet(PUNCH_ADS_ATTACH);
  if (id === 'rpg') return cloneAttachSet(RPG_ADS_ATTACH);
  if (id === 'ak47' || id === 'shotgun') return cloneAttachSet(AK47_ADS_ATTACH);
  if (id === 'gl') return cloneAttachSet(GL_ADS_ATTACH);
  if (id === 'sawedoff') return cloneAttachSet(SAWEDOFF_ADS_ATTACH);
  if (id === 'mp5') return cloneAttachSet(MP5_ADS_ATTACH);
  return cloneAttachSet(PISTOL_ADS_ATTACH);
}

export function bakedAdsAttachPose(id: WeaponId, side: HandSide): HandAttachPose {
  return clonePose(bakedAdsAttachSet(id)[side]);
}

export type ThumbJoint = [number, number, number];
export interface ThumbPose {
  /** Thenar / metacarpal, from the wrist. */
  base: ThumbJoint;
  /** Proximal phalanx. */
  mid: ThumbJoint;
  /** Distal / tip. */
  tip: ThumbJoint;
}

export function bakedThumbPose(id: WeaponId, side: HandSide): ThumbPose {
  if (id === 'punch') {
    if (side === 'R') {
      return {
        base: [-0.57, 0.75, 0.04],
        mid: [0.14, 0.07, -0.18],
        tip: [0.57, 1.11, -2.01],
      };
    }
    return {
      base: [-0.32, 0.86, -1.18],
      mid: [-0.29, 0.04, 0.68],
      tip: [0.43, -0.61, 1.94],
    };
  }
  if (side === 'R') {
    return {
      base: [-0.57, 0.75, 0.04],
      mid: [0.14, 0.07, -0.18],
      tip: [0.57, 1.11, -2.01],
    };
  }
  if (id === 'ak47' || id === 'shotgun') {
    return {
      base: [-0.61, 0.79, -3.15],
      mid: [-0.29, 0.04, 1.15],
      tip: [0.43, -0.61, 1.94],
    };
  }
  if (id === 'rpg') {
    return {
      base: [-1.11, 0.57, -1.18],
      mid: [-0.29, 0.04, 0.68],
      tip: [0.43, -0.61, 1.94],
    };
  }
  if (id === 'gl') {
    return {
      base: [-1.43, 0.86, -1.18],
      mid: [-0.29, 0.04, 0.68],
      tip: [0.14, 0.9, 1.94],
    };
  }
  if (id === 'sawedoff') {
    return {
      base: [-0.29, 0.32, 2.51],
      mid: [-0.18, -0.18, -0.14],
      tip: [0.25, 0.9, 2.29],
    };
  }
  if (id === 'mp5') {
    return {
      base: [-0.22, 0.32, -1.43],
      mid: [-0.29, 0.04, 1.15],
      tip: [0.43, -0.61, 1.94],
    };
  }
  return {
    base: [-0.32, 0.86, -1.18],
    mid: [-0.29, 0.04, 0.68],
    tip: [0.43, -0.61, 1.94],
  };
}

export function bakedThumbSet(id: WeaponId): { R: ThumbPose; L: ThumbPose } {
  return { R: bakedThumbPose(id, 'R'), L: bakedThumbPose(id, 'L') };
}

/** Thumb segment bulk: thenar attach + cross-section of all three parts. */
export interface ThenarShape {
  /** Thenar thickness across the palm (bone local X). */
  wide: number;
  /** Thenar thickness pad ↔ back (bone local Z). */
  flat: number;
  midWide: number;
  midFlat: number;
  tipWide: number;
  tipFlat: number;
  /** Distance out from palm center (mirrored per hand). */
  x: number;
  y: number;
  z: number;
  /** Right palm length toward the fingers (1 = default, lower = shorter). */
  palmLenR: number;
  /** Left palm length toward the fingers. */
  palmLenL: number;
}

export function defaultThenarShape(): ThenarShape {
  return {
    wide: 2.48,
    flat: 1.96,
    midWide: 1.4,
    midFlat: 1.36,
    tipWide: 1.82,
    tipFlat: 1.5,
    x: 0.011,
    y: -0.005,
    z: 0.001,
    palmLenR: 1,
    palmLenL: 1,
  };
}

const AK47_THENAR: ThenarShape = {
  wide: 2.48,
  flat: 2.1,
  midWide: 1.4,
  midFlat: 1.36,
  tipWide: 1.82,
  tipFlat: 1.5,
  x: 0.004,
  y: -0.003,
  z: -0.002,
  palmLenR: 1,
  palmLenL: 1,
};

const PUNCH_THENAR: ThenarShape = {
  wide: 2.48,
  flat: 1.96,
  midWide: 1.4,
  midFlat: 1.36,
  tipWide: 1.82,
  tipFlat: 1.5,
  x: 0.011,
  y: -0.005,
  z: 0.001,
  palmLenR: 0.89,
  palmLenL: 1,
};

const RPG_THENAR: ThenarShape = {
  wide: 2.4,
  flat: 1.96,
  midWide: 1.4,
  midFlat: 1.36,
  tipWide: 1.86,
  tipFlat: 1.5,
  x: 0.011,
  y: -0.005,
  z: 0.001,
  palmLenR: 1,
  palmLenL: 0.64,
};

const GL_THENAR: ThenarShape = {
  wide: 2.48,
  flat: 1.96,
  midWide: 1.4,
  midFlat: 1.36,
  tipWide: 1.82,
  tipFlat: 1.5,
  x: 0.011,
  y: -0.005,
  z: 0.001,
  palmLenR: 1,
  palmLenL: 0.72,
};

const SAWEDOFF_THENAR: ThenarShape = {
  wide: 2.48,
  flat: 1.96,
  midWide: 1.4,
  midFlat: 1.36,
  tipWide: 1.82,
  tipFlat: 1.5,
  x: 0.006,
  y: -0.005,
  z: 0.006,
  palmLenR: 1,
  palmLenL: 0.69,
};

function cloneThenar(s: ThenarShape): ThenarShape {
  return { ...s };
}

export function bakedThenarShape(id: WeaponId): ThenarShape {
  if (id === 'punch') return cloneThenar(PUNCH_THENAR);
  if (id === 'ak47' || id === 'shotgun') return cloneThenar(AK47_THENAR);
  if (id === 'rpg') return cloneThenar(RPG_THENAR);
  if (id === 'gl') return cloneThenar(GL_THENAR);
  if (id === 'sawedoff') return cloneThenar(SAWEDOFF_THENAR);
  return defaultThenarShape();
}

function paintSkin(): THREE.CanvasTexture {
  const s = 64;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#c4a07a';
  ctx.fillRect(0, 0, s, s);
  for (let i = 0; i < 180; i++) {
    const x = (i * 13) % s;
    const y = (i * 29) % s;
    ctx.fillStyle = i % 5 === 0 ? 'rgba(90,50,40,0.12)' : 'rgba(210,170,140,0.18)';
    ctx.fillRect(x, y, 1, 1);
  }
  ctx.fillStyle = 'rgba(140,70,70,0.14)';
  ctx.fillRect(8, 28, 48, 3);
  ctx.fillRect(18, 40, 28, 2);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(2, 2);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

function std(
  color: number,
  roughness: number,
  metalness = 0.04,
  map: THREE.Texture | null = null,
): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    map: map ?? undefined,
    color,
    roughness,
    metalness,
    envMapIntensity: 0.35,
    side: THREE.DoubleSide,
  });
}

function mark(obj: THREE.Object3D): void {
  obj.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
  });
}

function addCap(
  parent: THREE.Object3D,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  cylLen: number,
  radius: number,
  wide = 1,
  flat = 1,
  rx = Math.PI / 2,
  ry = 0,
  rz = 0,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CapsuleGeometry(radius, Math.max(0.0008, cylLen), 6, 10), mat);
  mesh.position.set(x, y, z);
  mesh.rotation.set(rx, ry, rz);
  mesh.scale.set(wide, 1, flat);
  parent.add(mesh);
  return mesh;
}

/** Grey sleeve: current wrist radius, thicker/longer toward the elbow (body). */
function addSleeveCuff(parent: THREE.Object3D, mat: THREE.Material): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.0165, 0.26, 10), mat);
  mesh.position.set(0, 0, 0.17);
  mesh.rotation.x = Math.PI / 2;
  mesh.scale.set(1.2, 1, 0.94);
  parent.add(mesh);
  return mesh;
}

interface Phalanx {
  len: number;
  r: number;
  wide: number;
  flat: number;
}

function addFinger(
  palm: THREE.Group,
  x: number,
  y: number,
  z: number,
  yaw: number,
  segs: Phalanx[],
  skin: THREE.Material,
  nail: THREE.Material,
  isThumb = false,
): Finger {
  const bones: THREE.Group[] = [];
  let parent: THREE.Object3D = palm;
  for (let i = 0; i < segs.length; i++) {
    const bone = new THREE.Group();
    const s = segs[i]!;
    if (i === 0) {
      bone.position.set(x, y, z);
      bone.rotation.y = yaw;
    } else {
      bone.position.z = -segs[i - 1]!.len;
    }
    const mesh = new THREE.Mesh(new THREE.CapsuleGeometry(s.r, Math.max(0.001, s.len * 0.72), 6, 10), skin);
    mesh.rotation.x = Math.PI / 2;
    mesh.position.z = -s.len * 0.48;
    mesh.scale.set(s.wide, 1, s.flat);
    bone.add(mesh);
    if (i === segs.length - 1) {
      const plate = new THREE.Mesh(
        new THREE.CapsuleGeometry(s.r * (isThumb ? 0.55 : 0.48), s.r * 0.08, 3, 8),
        nail,
      );
      plate.rotation.x = Math.PI / 2;
      plate.position.set(0, s.r * s.flat * 0.88, -s.len + s.r * 0.45);
      plate.scale.set(isThumb ? 1.4 : 1.28, 1, 0.26);
      bone.add(plate);
    }
    parent.add(bone);
    bones.push(bone);
    parent = bone;
  }
  return { bones, restYaw: yaw };
}

function setFingerPose(finger: Finger, pose: Pose3[], mirror = false): void {
  for (let i = 0; i < finger.bones.length; i++) {
    const p = pose[i] ?? [-0.45, 0, 0];
    const y = (i === 0 ? finger.restYaw : 0) + (mirror ? -p[1] : p[1]);
    const z = mirror ? -p[2] : p[2];
    finger.bones[i].rotation.set(p[0], y, z);
  }
}

/** Negative X curls toward the palm (−Y) so fingers wrap a grip, not splay open. */
const POSES: Record<ArmGripStyle, { r: Pose3[][]; rt: Pose3[]; l: Pose3[][]; lt: Pose3[] }> = {
  m9: {
    r: [
      [[-0.32, 0.02, 0.06], [-0.42, 0, 0], [-0.22, 0, 0]],
      [[-0.88, 0.01, 0.03], [-1.12, 0, 0], [-0.82, 0, 0]],
      [[-0.9, -0.01, 0.02], [-1.08, 0, 0], [-0.8, 0, 0]],
      [[-0.82, -0.03, 0.01], [-0.98, 0, 0], [-0.72, 0, 0]],
    ],
    rt: [[-0.57, 0.75, 0.04], [0.14, 0.07, -0.18], [0.57, 1.11, -2.01]],
    l: [
      [[-0.7, 0.04, 0.06], [-0.85, 0, 0], [-0.55, 0, 0]],
      [[-0.78, 0.02, 0.04], [-0.95, 0, 0], [-0.65, 0, 0]],
      [[-0.72, 0, 0.03], [-0.88, 0, 0], [-0.58, 0, 0]],
      [[-0.62, -0.02, 0.02], [-0.75, 0, 0], [-0.48, 0, 0]],
    ],
    lt: [[-0.32, 0.86, -1.18], [-0.29, 0.04, 0.68], [0.43, -0.61, 1.94]],
  },
  m870: {
    r: [
      [[-0.7, 0.03, 0.05], [-0.95, 0, 0], [-0.7, 0, 0]],
      [[-0.78, 0.01, 0.03], [-1.05, 0, 0], [-0.78, 0, 0]],
      [[-0.74, -0.01, 0.02], [-0.98, 0, 0], [-0.72, 0, 0]],
      [[-0.62, -0.03, 0.01], [-0.85, 0, 0], [-0.62, 0, 0]],
    ],
    rt: [[-0.1, 0.04, 0.06], [-0.18, 0.05, 0.08], [-0.12, 0.02, 0.03]],
    l: [
      [[-0.88, 0.03, 0.06], [-1.15, 0, 0], [-0.88, 0, 0]],
      [[-0.95, 0.01, 0.04], [-1.22, 0, 0], [-0.95, 0, 0]],
      [[-0.9, -0.01, 0.03], [-1.15, 0, 0], [-0.88, 0, 0]],
      [[-0.78, -0.03, 0.02], [-1.0, 0, 0], [-0.75, 0, 0]],
    ],
    lt: [[-0.08, 0.04, 0.06], [-0.16, 0.06, 0.1], [-0.12, 0.02, 0.04]],
  },
  sawedoff: {
    r: [
      [[-0.82, 0.03, 0.06], [-1.1, 0, 0], [-0.82, 0, 0]],
      [[-0.9, 0.01, 0.04], [-1.18, 0, 0], [-0.9, 0, 0]],
      [[-0.84, -0.01, 0.03], [-1.1, 0, 0], [-0.82, 0, 0]],
      [[-0.72, -0.03, 0.01], [-0.95, 0, 0], [-0.7, 0, 0]],
    ],
    rt: [[-0.1, 0.04, 0.06], [-0.18, 0.05, 0.08], [-0.12, 0.02, 0.03]],
    l: [
      [[-0.84, 0.03, 0.06], [-1.12, 0, 0], [-0.85, 0, 0]],
      [[-0.9, 0.01, 0.04], [-1.18, 0, 0], [-0.9, 0, 0]],
      [[-0.84, -0.01, 0.03], [-1.1, 0, 0], [-0.82, 0, 0]],
      [[-0.72, -0.03, 0.01], [-0.95, 0, 0], [-0.7, 0, 0]],
    ],
    lt: [[-0.08, 0.04, 0.06], [-0.16, 0.06, 0.1], [-0.12, 0.02, 0.04]],
  },
  longGun: {
    r: [
      [[-0.65, 0.02, 0.05], [-0.9, 0, 0], [-0.65, 0, 0]],
      [[-0.72, 0.01, 0.03], [-0.98, 0, 0], [-0.72, 0, 0]],
      [[-0.68, -0.01, 0.02], [-0.9, 0, 0], [-0.66, 0, 0]],
      [[-0.55, -0.03, 0.01], [-0.78, 0, 0], [-0.55, 0, 0]],
    ],
    rt: [[-0.08, 0.03, 0.05], [-0.16, 0.04, 0.08], [-0.1, 0.02, 0.03]],
    l: [
      [[-0.42, 0.03, 0.04], [-0.55, 0, 0], [-0.38, 0, 0]],
      [[-0.48, 0.01, 0.03], [-0.62, 0, 0], [-0.42, 0, 0]],
      [[-0.44, -0.01, 0.02], [-0.55, 0, 0], [-0.38, 0, 0]],
      [[-0.35, -0.03, 0.01], [-0.48, 0, 0], [-0.32, 0, 0]],
    ],
    lt: [[-0.08, 0.04, 0.05], [-0.14, 0.05, 0.08], [-0.1, 0.02, 0.03]],
  },
  fists: {
    r: [
      [[-1.05, 0.02, 0.04], [-1.25, 0, 0], [-0.95, 0, 0]],
      [[-1.12, 0.01, 0.03], [-1.32, 0, 0], [-1.02, 0, 0]],
      [[-1.08, -0.01, 0.02], [-1.28, 0, 0], [-0.98, 0, 0]],
      [[-0.98, -0.03, 0.01], [-1.18, 0, 0], [-0.88, 0, 0]],
    ],
    rt: [[-0.4, 0.55, 0.08], [0.2, 0.1, -0.2], [0.5, 0.9, -1.8]],
    l: [
      [[-1.05, 0.02, 0.04], [-1.25, 0, 0], [-0.95, 0, 0]],
      [[-1.12, 0.01, 0.03], [-1.32, 0, 0], [-1.02, 0, 0]],
      [[-1.08, -0.01, 0.02], [-1.28, 0, 0], [-0.98, 0, 0]],
      [[-0.98, -0.03, 0.01], [-1.18, 0, 0], [-0.88, 0, 0]],
    ],
    lt: [[-0.35, 0.7, -1.1], [-0.2, 0.05, 0.5], [0.4, -0.5, 1.7]],
  },
};

function scaleThumbSeg(bone: THREE.Group | undefined, wide: number, flat: number): void {
  if (!bone) return;
  const mesh = bone.children[0] as THREE.Mesh | undefined;
  if (mesh?.isMesh) mesh.scale.set(wide, 1, flat);
}

function makeLimbGeo(): THREE.CylinderGeometry {
  const geo = new THREE.CylinderGeometry(1, 1, 1, 10);
  geo.rotateX(Math.PI / 2);
  return geo;
}

export const PLAYER_SKIN_HEX = 0xc4a07a;
export const PLAYER_SLEEVE_HEX = 0x121214;

let _armMats: {
  skin: THREE.MeshStandardMaterial;
  nail: THREE.MeshStandardMaterial;
  sleeve: THREE.MeshStandardMaterial;
} | null = null;

/** Shared player skin / nail / sleeve mats (viewmodel + third-person arms). */
export function playerArmMaterials(): {
  skin: THREE.MeshStandardMaterial;
  nail: THREE.MeshStandardMaterial;
  sleeve: THREE.MeshStandardMaterial;
} {
  if (_armMats) return _armMats;
  const skinMap = paintSkin();
  _armMats = {
    skin: std(PLAYER_SKIN_HEX, 0.68, 0.03, skinMap),
    nail: new THREE.MeshStandardMaterial({
      color: 0xf3c8be,
      roughness: 0.3,
      metalness: 0.08,
      envMapIntensity: 0.35,
      transparent: true,
      opacity: 0.72,
    }),
    sleeve: std(PLAYER_SLEEVE_HEX, 0.82, 0.06),
  };
  return _armMats;
}

function poseFpsFingers(
  fingers: Finger[],
  thumb: Finger,
  isRight: boolean,
  style: ArmGripStyle,
): void {
  const p = POSES[style];
  if (isRight) {
    for (let i = 0; i < 4; i++) setFingerPose(fingers[i]!, p.r[i]!, true);
    setFingerPose(thumb, p.rt, true);
  } else {
    for (let i = 0; i < 4; i++) setFingerPose(fingers[i]!, p.l[i]!);
    setFingerPose(thumb, p.lt);
  }
}

/**
 * First-person hand (palm + fingers + wrist/sleeve cuff).
 * Local frame: +Y back of hand, +Z wrist, −Z fingers.
 * Parent to an enemy hand bone with `rotation.x = π/2` so fingers follow +Y.
 */
export function createFpsHandGroup(
  isRight: boolean,
  style: ArmGripStyle,
  sleeveMat?: THREE.Material,
): THREE.Group {
  const { skin, nail, sleeve } = playerArmMaterials();
  const cuff = sleeveMat ?? sleeve;
  const side = isRight ? -1 : 1;
  const hand = new THREE.Group();
  hand.name = isRight ? 'fpsHandR' : 'fpsHandL';
  const wrist = new THREE.Group();
  const palm = new THREE.Group();
  hand.add(wrist);
  wrist.add(palm);

  const palmCap = addCap(palm, skin, 0, -0.001, -0.028, 0.05, 0.013, 2.15, 0.7);
  const fingers: Finger[] = [];
  const spreads = [0.07, 0.02, -0.04, -0.13];
  const xs = [0.018, 0.006, -0.007, -0.019];
  const digits: Phalanx[][] = [
    [
      { len: 0.032, r: 0.0062, wide: 1.28, flat: 0.72 },
      { len: 0.02, r: 0.0054, wide: 1.2, flat: 0.7 },
      { len: 0.016, r: 0.0045, wide: 1.14, flat: 0.66 },
    ],
    [
      { len: 0.036, r: 0.0069, wide: 1.36, flat: 0.74 },
      { len: 0.024, r: 0.0059, wide: 1.28, flat: 0.71 },
      { len: 0.017, r: 0.0048, wide: 1.18, flat: 0.67 },
    ],
    [
      { len: 0.033, r: 0.006, wide: 1.24, flat: 0.71 },
      { len: 0.021, r: 0.0051, wide: 1.16, flat: 0.68 },
      { len: 0.015, r: 0.0043, wide: 1.1, flat: 0.65 },
    ],
    [
      { len: 0.024, r: 0.0048, wide: 1.1, flat: 0.68 },
      { len: 0.015, r: 0.0041, wide: 1.04, flat: 0.64 },
      { len: 0.012, r: 0.0035, wide: 0.98, flat: 0.6 },
    ],
  ];
  for (let i = 0; i < 4; i++) {
    fingers.push(addFinger(palm, xs[i]! * side, 0.001, -0.05, spreads[i]! * side, digits[i]!, skin, nail));
  }
  const thenar = defaultThenarShape();
  const thumb = addFinger(
    palm,
    side * thenar.x,
    thenar.y,
    thenar.z,
    side * (-Math.PI / 2 + 0.42),
    [
      { len: 0.032, r: 0.005, wide: thenar.wide, flat: thenar.flat },
      { len: 0.019, r: 0.0048, wide: 1.1, flat: 0.76 },
      { len: 0.012, r: 0.0034, wide: 1.0, flat: 0.64 },
    ],
    skin,
    nail,
    true,
  );
  addCap(hand, skin, 0, 0, 0.022, 0.018, 0.013, 1.2, 0.85);
  addSleeveCuff(hand, cuff);

  const t = style === 'fists' ? 0.89 : 1;
  palmCap.scale.set(2.15, t, 0.7);
  palmCap.position.z = -0.028 + 0.038 * (1 - t);
  const fingerZ = 0.026 - 0.076 * t;
  for (const f of fingers) f.bones[0]!.position.z = fingerZ;

  poseFpsFingers(fingers, thumb, isRight, style);
  hand.userData.fpsFingers = fingers;
  hand.userData.fpsThumb = thumb;
  return hand;
}

export class ProcArms {
  readonly root = new THREE.Group();
  readonly attach: Record<WeaponId, HandAttachSet> = {
    pistol: bakedAttachSet('pistol'),
    shotgun: bakedAttachSet('shotgun'),
    sawedoff: bakedAttachSet('sawedoff'),
    mp5: bakedAttachSet('mp5'),
    ak47: bakedAttachSet('ak47'),
    punch: bakedAttachSet('punch'),
    gl: bakedAttachSet('gl'),
    rpg: bakedAttachSet('rpg'),
  };
  readonly attachAds: Record<WeaponId, HandAttachSet> = {
    pistol: bakedAdsAttachSet('pistol'),
    shotgun: bakedAdsAttachSet('shotgun'),
    sawedoff: bakedAdsAttachSet('sawedoff'),
    mp5: bakedAdsAttachSet('mp5'),
    ak47: bakedAdsAttachSet('ak47'),
    punch: bakedAdsAttachSet('punch'),
    gl: bakedAdsAttachSet('gl'),
    rpg: bakedAdsAttachSet('rpg'),
  };
  readonly thumb: Record<WeaponId, { R: ThumbPose; L: ThumbPose }> = {
    pistol: bakedThumbSet('pistol'),
    shotgun: bakedThumbSet('shotgun'),
    sawedoff: bakedThumbSet('sawedoff'),
    mp5: bakedThumbSet('mp5'),
    ak47: bakedThumbSet('ak47'),
    punch: bakedThumbSet('punch'),
    gl: bakedThumbSet('gl'),
    rpg: bakedThumbSet('rpg'),
  };
  readonly thenar: Record<WeaponId, ThenarShape> = {
    pistol: bakedThenarShape('pistol'),
    shotgun: bakedThenarShape('shotgun'),
    sawedoff: bakedThenarShape('sawedoff'),
    mp5: bakedThenarShape('mp5'),
    ak47: bakedThenarShape('ak47'),
    punch: bakedThenarShape('punch'),
    gl: bakedThenarShape('gl'),
    rpg: bakedThenarShape('rpg'),
  };
  private weaponId: WeaponId = 'pistol';
  private style: ArmGripStyle = 'm9';
  private ads = 0;
  private readonly right: HandRig;
  private readonly left: HandRig;
  private readonly swingTarget = new THREE.Object3D();
  private readonly hipEuler = new THREE.Euler(0, 0, 0, 'YXZ');
  private readonly adsEuler = new THREE.Euler(0, 0, 0, 'YXZ');
  private readonly hipQuat = new THREE.Quaternion();
  private readonly adsQuat = new THREE.Quaternion();
  readonly reloadPalm: HandAttachPose = bakedReloadPalm();

  constructor(mats?: { skin?: THREE.Material; nail?: THREE.Material; sleeve?: THREE.Material }) {
    const base = playerArmMaterials();
    const skin = mats?.skin ?? base.skin;
    const nail = mats?.nail ?? base.nail;
    const sleeve = mats?.sleeve ?? base.sleeve;

    this.right = this.buildHand(true, skin, nail, sleeve);
    this.left = this.buildHand(false, skin, nail, sleeve);

    this.root.name = 'procArms';
    this.root.frustumCulled = false;
    this.root.add(this.right.hand);
    this.root.add(this.left.hand);
    this.root.add(this.right.forearm);
    this.root.add(this.left.forearm);
    this.root.add(this.right.sleeve);
    this.root.add(this.left.sleeve);
    this.root.add(this.right.shoulder);
    this.root.add(this.left.shoulder);
    this.root.add(this.swingTarget);
    this.useWeapon('pistol');
    mark(this.root);
  }

  useWeapon(id: WeaponId): void {
    this.weaponId = id;
    this.setStyle(armStyleForWeapon(id));
  }

  setStyle(style: ArmGripStyle): void {
    this.style = style;
    this.refreshFingers();
    this.applyThenar();
  }

  poseOf(side: HandSide, id: WeaponId = this.weaponId, ads = false): HandAttachPose {
    return (ads ? this.attachAds : this.attach)[id][side];
  }

  thumbPoseOf(side: HandSide, id: WeaponId = this.weaponId): ThumbPose {
    return this.thumb[id][side];
  }

  thenarShape(id: WeaponId = this.weaponId): ThenarShape {
    return this.thenar[id];
  }

  applyThenar(shape?: ThenarShape): void {
    const cur = this.thenar[this.weaponId];
    if (shape) Object.assign(cur, shape);
    this.placeThenar(this.right, -1, cur);
    this.placeThenar(this.left, 1, cur);
  }

  resetThenar(id: WeaponId = this.weaponId): void {
    this.thenar[id] = bakedThenarShape(id);
    if (id === this.weaponId) this.applyThenar();
  }

  resetAttach(id: WeaponId = this.weaponId, side?: HandSide, ads?: boolean): void {
    if (ads === true) {
      if (side) this.attachAds[id][side] = bakedAdsAttachPose(id, side);
      else this.attachAds[id] = bakedAdsAttachSet(id);
    } else if (ads === false) {
      if (side) this.attach[id][side] = bakedAttachPose(id, side);
      else this.attach[id] = bakedAttachSet(id);
    } else {
      if (side) {
        this.attach[id][side] = bakedAttachPose(id, side);
        this.attachAds[id][side] = bakedAdsAttachPose(id, side);
      } else {
        this.attach[id] = bakedAttachSet(id);
        this.attachAds[id] = bakedAdsAttachSet(id);
      }
    }
    if (id === this.weaponId) this.setStyle(this.style);
  }

  resetThumb(id: WeaponId = this.weaponId, side?: HandSide): void {
    if (side) this.thumb[id][side] = bakedThumbPose(id, side);
    else this.thumb[id] = bakedThumbSet(id);
    if (id === this.weaponId) this.setStyle(this.style);
  }

  resetReloadPalm(): void {
    Object.assign(this.reloadPalm, bakedReloadPalm());
    if (this.weaponId === 'sawedoff') this.setStyle(this.style);
  }

  update(
    gripR: THREE.Object3D | null,
    gripL: THREE.Object3D | null,
    sprintBlend: number,
    sprintPhase: number,
    adsBlend = 0,
    leftAttach: HandAttachPose | null = null,
  ): void {
    this.ads = adsBlend;
    this.refreshFingers();
    if (leftAttach) {
      const hip = this.attach[this.weaponId].L;
      this.addCurl(this.left, leftAttach.curl - hip.curl, leftAttach.knuckle - hip.knuckle, false);
    }
    if (gripR) this.glueHand(this.right, gripR, 'R');
    else this.hideRig(this.right, true);

    const swing = sprintBlend > 0.25;
    if (swing) {
      const a = Math.sin(sprintPhase);
      this.swingTarget.position.set(-0.1 + a * 0.05, -0.2 + Math.abs(a) * 0.04, -0.14 + a * 0.16);
      this.swingTarget.quaternion.identity();
      this.glueHand(this.left, this.swingTarget, 'L');
    } else if (gripL) {
      this.glueHand(this.left, gripL, 'L', leftAttach);
    } else {
      this.hideRig(this.left, true);
    }
  }

  private buildHand(
    isRight: boolean,
    skin: THREE.Material,
    nail: THREE.Material,
    sleeve: THREE.Material,
  ): HandRig {
    const sign = isRight ? 1 : -1;
    const side = isRight ? -1 : 1;
    const hand = new THREE.Group();
    hand.name = isRight ? 'procHandR' : 'procHandL';
    const wrist = new THREE.Group();
    const palm = new THREE.Group();
    hand.add(wrist);
    wrist.add(palm);

    const palmCap = addCap(palm, skin, 0, -0.001, -0.028, 0.05, 0.013, 2.15, 0.7);

    const fingers: Finger[] = [];
    const spreads = [0.07, 0.02, -0.04, -0.13];
    const xs = [0.018, 0.006, -0.007, -0.019];
    const digits: Phalanx[][] = [
      [
        { len: 0.032, r: 0.0062, wide: 1.28, flat: 0.72 },
        { len: 0.02, r: 0.0054, wide: 1.2, flat: 0.7 },
        { len: 0.016, r: 0.0045, wide: 1.14, flat: 0.66 },
      ],
      [
        { len: 0.036, r: 0.0069, wide: 1.36, flat: 0.74 },
        { len: 0.024, r: 0.0059, wide: 1.28, flat: 0.71 },
        { len: 0.017, r: 0.0048, wide: 1.18, flat: 0.67 },
      ],
      [
        { len: 0.033, r: 0.006, wide: 1.24, flat: 0.71 },
        { len: 0.021, r: 0.0051, wide: 1.16, flat: 0.68 },
        { len: 0.015, r: 0.0043, wide: 1.1, flat: 0.65 },
      ],
      [
        { len: 0.024, r: 0.0048, wide: 1.1, flat: 0.68 },
        { len: 0.015, r: 0.0041, wide: 1.04, flat: 0.64 },
        { len: 0.012, r: 0.0035, wide: 0.98, flat: 0.6 },
      ],
    ];
    for (let i = 0; i < 4; i++) {
      fingers.push(addFinger(palm, xs[i]! * side, 0.001, -0.05, spreads[i]! * side, digits[i]!, skin, nail));
    }
    const thenar = defaultThenarShape();
    const thumb = addFinger(
      palm,
      side * thenar.x,
      thenar.y,
      thenar.z,
      side * (-Math.PI / 2 + 0.42),
      [
        { len: 0.032, r: 0.005, wide: thenar.wide, flat: thenar.flat },
        { len: 0.019, r: 0.0048, wide: 1.1, flat: 0.76 },
        { len: 0.012, r: 0.0034, wide: 1.0, flat: 0.64 },
      ],
      skin,
      nail,
      true,
    );

    addCap(hand, skin, 0, 0, 0.022, 0.018, 0.013, 1.2, 0.85);
    addSleeveCuff(hand, sleeve);

    const forearm = new THREE.Mesh(makeLimbGeo(), skin);
    const sleeveMesh = new THREE.Mesh(makeLimbGeo(), sleeve);
    const shoulder = new THREE.Mesh(new THREE.SphereGeometry(0.028, 10, 8), sleeve);
    forearm.visible = false;
    sleeveMesh.visible = false;
    shoulder.visible = false;

    return {
      hand,
      wrist,
      palm,
      palmCap,
      fingers,
      thumb,
      forearm,
      sleeve: sleeveMesh,
      shoulder,
      shoulderLocal: new THREE.Vector3(sign * 0.22, -0.42, -0.04),
      palmBindZ: 0.006,
    };
  }

  private placeThenar(rig: HandRig, side: number, s: ThenarShape): void {
    const bone0 = rig.thumb.bones[0];
    if (bone0) {
      bone0.position.set(side * s.x, s.y, s.z);
      scaleThumbSeg(bone0, s.wide, s.flat);
    }
    scaleThumbSeg(rig.thumb.bones[1], s.midWide, s.midFlat);
    scaleThumbSeg(rig.thumb.bones[2], s.tipWide, s.tipFlat);
    const t = side < 0 ? s.palmLenR : s.palmLenL;
    rig.palmCap.scale.set(2.15, t, 0.7);
    rig.palmCap.position.z = -0.028 + 0.038 * (1 - t);
    const fingerZ = 0.026 - 0.076 * t;
    for (const f of rig.fingers) {
      f.bones[0]!.position.z = fingerZ;
    }
  }

  private refreshFingers(): void {
    const p = POSES[this.style];
    for (let i = 0; i < 4; i++) {
      setFingerPose(this.right.fingers[i]!, p.r[i]!, true);
      setFingerPose(this.left.fingers[i]!, p.l[i]!);
    }
    const th = this.thumb[this.weaponId];
    setFingerPose(this.right.thumb, [th.R.base, th.R.mid, th.R.tip], true);
    setFingerPose(this.left.thumb, [th.L.base, th.L.mid, th.L.tip]);
    const hip = this.attach[this.weaponId];
    const ads = this.attachAds[this.weaponId];
    const t = this.ads;
    this.addCurl(this.right, lerp(hip.R.curl, ads.R.curl, t), lerp(hip.R.knuckle, ads.R.knuckle, t), true);
    this.addCurl(this.left, lerp(hip.L.curl, ads.L.curl, t), lerp(hip.L.knuckle, ads.L.knuckle, t), false);
  }

  private addCurl(rig: HandRig, extra: number, knuckle: number, isRight: boolean): void {
    if (extra === 0 && knuckle === 0) return;
    const fingerScale = isRight ? [0.52, 1, 1.22, 1.38] : [1, 1, 1, 1];
    for (let i = 0; i < rig.fingers.length; i++) {
      const scale = fingerScale[i] ?? 1;
      const bones = rig.fingers[i]!.bones;
      for (let j = 0; j < bones.length; j++) {
        let add = extra * scale;
        if (j === 0 && (i === 2 || i === 3)) add += knuckle;
        bones[j]!.rotation.x += add;
      }
    }
    for (let i = 0; i < rig.thumb.bones.length; i++) {
      const scale = isRight ? [0.12, 0.16, 0][i] ?? 0 : 0.12;
      rig.thumb.bones[i]!.rotation.x += extra * scale;
    }
  }

  private glueHand(
    rig: HandRig,
    grip: THREE.Object3D,
    side: HandSide,
    poseOverride: HandAttachPose | null = null,
  ): void {
    rig.hand.visible = true;
    rig.forearm.visible = false;
    rig.sleeve.visible = false;
    rig.shoulder.visible = false;

    if (rig.hand.parent !== grip) grip.add(rig.hand);
    rig.hand.rotation.order = 'YXZ';
    rig.wrist.rotation.order = 'YXZ';
    if (poseOverride) {
      rig.hand.position.set(poseOverride.x, poseOverride.y, poseOverride.z);
      rig.hand.rotation.set(poseOverride.rx, poseOverride.ry, poseOverride.rz);
      rig.hand.scale.setScalar(poseOverride.scale);
      rig.wrist.rotation.set(poseOverride.wx, poseOverride.wy, poseOverride.wz);
      return;
    }
    const hip = this.attach[this.weaponId][side];
    const ads = this.attachAds[this.weaponId][side];
    const t = this.ads;
    rig.hand.position.set(lerp(hip.x, ads.x, t), lerp(hip.y, ads.y, t), lerp(hip.z, ads.z, t));
    this.hipEuler.set(hip.rx, hip.ry, hip.rz, 'YXZ');
    this.adsEuler.set(ads.rx, ads.ry, ads.rz, 'YXZ');
    this.hipQuat.setFromEuler(this.hipEuler);
    this.adsQuat.setFromEuler(this.adsEuler);
    rig.hand.quaternion.slerpQuaternions(this.hipQuat, this.adsQuat, t);
    rig.hand.scale.setScalar(lerp(hip.scale, ads.scale, t));
    rig.wrist.rotation.set(
      lerp(hip.wx, ads.wx, t),
      lerp(hip.wy, ads.wy, t),
      lerp(hip.wz, ads.wz, t),
    );
  }

  private hideRig(rig: HandRig, hide: boolean): void {
    if (hide && rig.hand.parent !== this.root) this.root.add(rig.hand);
    rig.hand.visible = !hide;
    rig.forearm.visible = false;
    rig.sleeve.visible = false;
    rig.shoulder.visible = false;
  }
}
